import { Injectable } from "@angular/core";
import { ApolloQueryResult } from "@apollo/client/core";
import { SpinnerService } from "@saliente/library";
import { plainToClass } from "class-transformer";
import { Observable, of, Subject } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { Accounts_ClientFormSpecRepresentationInput, Accounts_DuAnswers2024RepresentationInput, Accounts_DuAnswers2025RepresentationInput, Accounts_DuAnswersRepresentationInput, Accounts_MicroProfitAnswersRepresentationInput } from "src/graphql";
import { AuthService } from "../common/auth/auth.service";
import { ConfigService } from "../common/general/config.service";
import { DownloadService } from "../common/general/download.service";
import { KMutationOptions, RestService } from "../common/general/rest.service";
import { GetCaenCodesDocument, GetCaenCodesQuery, GetClientFiscalInfoSummaryForMonthGQL, GetClientFormDuPfaKeez2024DataDocument, GetClientFormDuPfaKeez2024DataQuery, GetClientFormDuPfaKeez2025DataDocument, GetClientFormDuPfaKeez2025DataQuery, GetClientFormDuPfaKeezDataDocument, GetClientFormDuPfaKeezDataQuery, GetClientFormMicroProfitDataDocument, GetClientFormMicroProfitDataQuery, GetClientFormsDataDocument, GetClientFormsDataQuery, GetFormComponentsGQL, InvalidateClientFormGQL, UpdateClientFormDuPfaKeez2024DataGQL, UpdateClientFormDuPfaKeez2025DataGQL, UpdateClientFormDuPfaKeezDataGQL, UpdateClientFormMicroProfitDataGQL } from "./adapters/clientforms.generated";
import { CaenCodeModel, ClientFormDataModel, ClientFormListModel, ClientFormModel, DeclaratiaUnicaPfaModel, DuActivitatiIndependente2025Model, DuActivitatiIndependenteModel, DuAnswers2024EstimatCASSModel, DuChiriiModel, DuCoasigurat2025Model, DuObligRealizat2025Model, DuPerson2025Model, DuPfa2024DataModel, DuPfa2024Model, DuPfa2025DataModel, DuPfa2025Model, DuPfaDataModel, DuPfaParametersModel, FormComponentModel, FormStatus, MicroProfitDataModel, MicroProfitFormModel, MicroProfitParametersModel, SistemImpozitareActivitatiIndependente, SistemImpozitareChirii } from "./client-forms.models";

@Injectable()
export class ClientFormsService {
    public isFormTouched: Subject<void> = new Subject<void>();

    constructor(private authService: AuthService, private restService: RestService, private spinner: SpinnerService, private configService: ConfigService,
            private downloadService: DownloadService) { }
    
    public getMicroProfitData(clientEid: string, instance: string): Observable<MicroProfitFormModel> {
        this.spinner.show();
        return this.restService
            .query({
                query: GetClientFormMicroProfitDataDocument,
                variables: {
                    clientEid: clientEid,
                    instance: instance,
                },
                fetchPolicy: 'network-only'
            }, { spinner: true })
            .pipe(
                map((response: ApolloQueryResult<GetClientFormMicroProfitDataQuery>) => {
                    let result: MicroProfitFormModel = null;
                    try {
                        result = plainToClass(MicroProfitFormModel, response.data.accounts.getClientFormMicroProfitDataUsingGET);
                        result.data = plainToClass(MicroProfitDataModel, result.data);
                        if (result.data.micro3p)
                            result.data.hasCaenCode = true;
                        if (result.data.micro1p)
                            result.data.hasCaenCode = false;
                        result.formData = plainToClass(ClientFormDataModel, result.formData);
                        result.formDataParameters = plainToClass(MicroProfitParametersModel, result.formData.parameters);

                        this.applyMicroProfitFormDefaults(result.data);
                    } catch {
                        result = null;
                    }
                    this.spinner.hide();
                    return result;
                })
            );
    }

    public applyMicroProfitFormDefaults(data: MicroProfitDataModel) {
        data.are1FTE = data.are1FTE == null ? data.defaultAre1FTE : data.are1FTE;
        data.are253Micro = data.are253Micro == null ? data.defaultAre253Micro : data.are253Micro;
        data.areConsultanta80 = data.areConsultanta80 == null ? data.defaultAreConsultanta80 : data.areConsultanta80;
    }

    public async updateClientFormMicroProfitData(clientEid: string, instance: string, data: MicroProfitDataModel): Promise<boolean> {
		let mutation = new UpdateClientFormMicroProfitDataGQL(this.restService);

        let mutationData: Accounts_MicroProfitAnswersRepresentationInput = {};
        mutationData.are1FTE = data.are1FTE;
        mutationData.areConsultanta80 = data.areConsultanta80;
        mutationData.caSub500k = data.caSub500k;
        mutationData.are253Micro = data.are253Micro;
        mutationData.pastreazaFirmaMicro = data.pastreazaFirmaMicro;

        mutationData.ramaneMicro = data.ramaneMicro;
        mutationData.doresteMicro = data.doresteMicro;
        mutationData.devineMicro = data.devineMicro;
        mutationData.ramaneProfit = data.ramaneProfit;
        mutationData.devineProfit = data.devineProfit;
        mutationData.micro1p = data.micro1p;
        mutationData.micro3p = data.micro3p;

        let result = await mutation.mutate({
			clientEid: clientEid,
			instance: instance,
            data: mutationData
		}, { error: true } as KMutationOptions).toPromise();

        if (result) {
			return true;
		}
		
		return false;
    }

    public sistemImpozitareActivitatiIndependenteDisplay(sistem: string): string {
		switch (sistem) {
			case SistemImpozitareActivitatiIndependente.SistemReal:
				return 'Sistem real';
			case SistemImpozitareActivitatiIndependente.CoteForfetareDeCheltuieli:
				return 'Cote forfetare de cheltuieli';
			case SistemImpozitareActivitatiIndependente.NormaDeVenit:
				return 'Normă de venit';
            default:
                return '';
        }
	}

    public sistemImpozitareChiriiDisplay(sistem: string): string {
		switch (sistem) {
			case SistemImpozitareChirii.SistemReal:
				return 'Sistem real';
			case SistemImpozitareChirii.CoteForfetare:
				return 'Cote forfetare';
            default:
                return '';
        }
	}

    public getCaenCodeList(): Observable<CaenCodeModel[]> {
        return this.restService
            .query({
                query: GetCaenCodesDocument,
                fetchPolicy: 'network-only'
            }, { spinner: false })
            .pipe(
                map((response: ApolloQueryResult<GetCaenCodesQuery>) => {
                    let result: CaenCodeModel[] = [] ;
                    try {
                        result = plainToClass<CaenCodeModel, object>(CaenCodeModel, response.data.accounts.getCaenCodesUsingGET || []);
                    } catch {
                        result = [];
                    }
                    
                    return result;
                })
            );
    }

    public async getClientFiscalInfoSummaryForMonth(clientEid: string, forMonth: number): Promise<string> {
		let query = new GetClientFiscalInfoSummaryForMonthGQL(this.restService);
		let queryData = await query.fetch(
			{
				clientEid: clientEid,
                forMonth: forMonth
			}
		).toPromise();

		if (queryData != null) {
			return queryData.data.accounts.getClientFiscalInfoSummaryForMonthUsingGET.profitMicroCode;
		}

		return null;
	}

    public getDuPfaKeezData(clientEid: string, instance: string): Observable<DeclaratiaUnicaPfaModel> {
        this.spinner.show();
        return this.restService
            .query({
                query: GetClientFormDuPfaKeezDataDocument,
                variables: {
                    clientEid: clientEid,
                    instance: instance,
                },
                fetchPolicy: 'network-only'
            }, { spinner: true })
            .pipe(
                map((response: ApolloQueryResult<GetClientFormDuPfaKeezDataQuery>) => {
                    let result: DeclaratiaUnicaPfaModel = null;
                    try {
                        result = plainToClass(DeclaratiaUnicaPfaModel, response.data.accounts.getClientFormDuPfaKeezDataUsingGET);
                        result.data = plainToClass(DuPfaDataModel, result.data);
                        result.data.activitatiIndependenteEstimat = plainToClass<DuActivitatiIndependenteModel, object>(DuActivitatiIndependenteModel, result.data.activitatiIndependenteEstimat || []);
                        result.data.activitatiIndependenteRealizat = plainToClass<DuActivitatiIndependenteModel, object>(DuActivitatiIndependenteModel, result.data.activitatiIndependenteRealizat || []);
                        result.data.chiriiEstimat = plainToClass<DuChiriiModel, object>(DuChiriiModel, result.data.chiriiEstimat || []);
                        result.data.chiriiRealizat = plainToClass<DuChiriiModel, object>(DuChiriiModel, result.data.chiriiRealizat || []);
                        result.formData = plainToClass(ClientFormDataModel, result.formData);
                        result.formDataParameters = plainToClass(DuPfaParametersModel, result.formData.parameters);

                        this.applyDuPFAFormDefaults(result.data);
                    } catch {
                        result = null;
                    }
                    this.spinner.hide();
                    return result;
                })
            );
    }

    public applyDuPFAFormDefaults(data: DuPfaDataModel) {
        data.optionalPensieEstimat = data.optionalPensieEstimat ?? false;
        data.optionalPensieRealizat = data.optionalPensieRealizat ?? false;
        data.optionalSanatateEstimat = data.optionalSanatateEstimat ?? false;
        data.optionalSanatateRealizat = data.optionalSanatateRealizat ?? false;
        data.impozitONG = data.impozitONG ?? false;
    }

    public async updateClientFormDupfaKeezData(clientEid: string, instance: string, doSubmit: boolean, data: DuPfaDataModel): Promise<boolean> {
		let mutation = new UpdateClientFormDuPfaKeezDataGQL(this.restService);

        let mutationData: Accounts_DuAnswersRepresentationInput = {
            persoana: {},
            activitatiIndependenteEstimat: [],
            activitatiIndependenteRealizat: [],
            chiriiEstimat: [],
            chiriiRealizat: []
        };

        mutationData.persoana.adresa = data.persoana.adresa;
        mutationData.persoana.areHandicap = data.persoana.areHandicap;
        mutationData.persoana.cnp = data.persoana.cnp;
        mutationData.persoana.email = data.persoana.email;
        mutationData.persoana.iban = data.persoana.iban;
        mutationData.persoana.nerezident = data.persoana.nerezident;
        mutationData.persoana.nume = data.persoana.nume;
        mutationData.persoana.prenume = data.persoana.prenume;
        mutationData.persoana.telefon = data.persoana.telefon;

        mutationData.agriEstimat = data.agriEstimat;
        mutationData.agriRealizat = data.agriRealizat;
        mutationData.alteSurseEstimat = data.alteSurseEstimat;
        mutationData.alteSurseRealizat = data.alteSurseRealizat;
        mutationData.anEstimat = data.anEstimat;
        mutationData.anRealizat = data.anRealizat;
        mutationData.cimNedeterminat = data.cimNedeterminat;
        mutationData.comments = data.comments;
        mutationData.dividendeEstimat = data.dividendeEstimat;
        mutationData.dividendeRealizat = data.dividendeRealizat;
        mutationData.dreptAutorEstimat = data.dreptAutorEstimat;
        mutationData.dreptAutorRealizat = data.dreptAutorRealizat;
        mutationData.impozitONG = data.impozitONG;
        mutationData.ongCIF = data.ongCIF;
        mutationData.ongIBAN = data.ongIBAN;
        mutationData.optionalPensieEstimat = data.optionalPensieEstimat;
        mutationData.optionalPensieRealizat = data.optionalPensieRealizat;
        mutationData.optionalSanatateEstimat = data.optionalSanatateEstimat;
        mutationData.optionalSanatateRealizat = data.optionalSanatateRealizat;
        mutationData.optiunePensieValoareEstimat = data.optiunePensieValoareEstimat;
        mutationData.rectificativaEstimat = data.rectificativaEstimat;
        mutationData.rectificativaRealizat = data.rectificativaRealizat;
        mutationData.titluriValoareEstimat = data.titluriValoareEstimat;
        mutationData.titluriValoareRealizat = data.titluriValoareRealizat;
        mutationData.venituriStrainatate = data.venituriStrainatate;

        data.activitatiIndependenteEstimat.forEach((activitate) => {
            mutationData.activitatiIndependenteEstimat.push({
				cheltuieliDeductibile: activitate.cheltuieliDeductibile,
                codCaen: activitate.codCaen,
                denCaen: activitate.denCaen,
                dataDocument: activitate.dataDocument,
                dataInceperiiActivitatii: activitate.dataInceperiiActivitatii,
                dataIncheieriiActivitatii: activitate.dataIncheieriiActivitatii,
                formaOrganizare: activitate.formaOrganizare,
                normaDeVenit: activitate.normaDeVenit,
                numarDocument: activitate.numarDocument,
                sediuSauIdentificareBun: activitate.sediuSauIdentificareBun,
                sistemImpozitare: activitate.sistemImpozitare,
                venitBrut: activitate.venitBrut
			});
        });

        data.activitatiIndependenteRealizat.forEach((activitate) => {
            mutationData.activitatiIndependenteRealizat.push({
				cheltuieliDeductibile: activitate.cheltuieliDeductibile,
                codCaen: activitate.codCaen,
                denCaen: activitate.denCaen,
                dataDocument: activitate.dataDocument,
                dataInceperiiActivitatii: activitate.dataInceperiiActivitatii,
                dataIncheieriiActivitatii: activitate.dataIncheieriiActivitatii,
                formaOrganizare: activitate.formaOrganizare,
                normaDeVenit: activitate.normaDeVenit,
                numarDocument: activitate.numarDocument,
                sediuSauIdentificareBun: activitate.sediuSauIdentificareBun,
                sistemImpozitare: activitate.sistemImpozitare,
                venitBrut: activitate.venitBrut
			});
        });

        data.chiriiEstimat.forEach((chirie) => {
            mutationData.chiriiEstimat.push({
                adresa: chirie.adresa,
                dataDocument: chirie.dataDocument,
                dataInceperiiActivitatii: chirie.dataInceperiiActivitatii,
                dataIncetariiActivitatii: chirie.dataIncetariiActivitatii,
                numarDocument: chirie.numarDocument,
                sistemImpozitare: chirie.sistemImpozitare,
                venitBrut: chirie.venitBrut,
                cheltuieliDeductibile: chirie.cheltuieliDeductibile
            })
        });

        data.chiriiRealizat.forEach((chirie) => {
            mutationData.chiriiRealizat.push({
                adresa: chirie.adresa,
                dataDocument: chirie.dataDocument,
                dataInceperiiActivitatii: chirie.dataInceperiiActivitatii,
                dataIncetariiActivitatii: chirie.dataIncetariiActivitatii,
                numarDocument: chirie.numarDocument,
                sistemImpozitare: chirie.sistemImpozitare,
                venitBrut: chirie.venitBrut,
                cheltuieliDeductibile: chirie.cheltuieliDeductibile
            })
        });

        let result = await mutation.mutate({
			clientEid: clientEid,
			instance: instance,
            doSubmit: doSubmit,
            data: mutationData
		}, { error: true } as KMutationOptions).toPromise();

        if (result) {
			return true;
		}
		
		return false;
    }

    public async invalidateClientForm(clientEid: string, form: string, instance: string) {
        let mutation = new InvalidateClientFormGQL(this.restService);

        let mutationData: Accounts_ClientFormSpecRepresentationInput = {
            form: form,
            instance: instance
        };

        let result = await mutation.mutate({
			clientEid: clientEid,
            body: mutationData
		}, { error: true } as KMutationOptions).toPromise();

        if (result) {
			return true;
		}
		
		return false;
    }

    // Du PFA 2024
    public getDuPfaKeez2024Data(clientEid: string): Observable<DuPfa2024Model> {
        this.spinner.show();
        return this.restService
            .query({
                query: GetClientFormDuPfaKeez2024DataDocument,
                variables: {
                    clientEid: clientEid,
                },
                fetchPolicy: 'network-only'
            }, { spinner: true })
            .pipe(
                map((response: ApolloQueryResult<GetClientFormDuPfaKeez2024DataQuery>) => {
                    let result: DuPfa2024Model = null;
                    try {
                        result = plainToClass(DuPfa2024Model, response.data.accounts.getClientFormDuPfaKeez2024DataUsingGET);
                        result.data = plainToClass(DuPfa2024DataModel, result.data);
                        result.data.activitatiIndependenteRealizat = plainToClass<DuActivitatiIndependenteModel, object>(DuActivitatiIndependenteModel, result.data.activitatiIndependenteRealizat || []);
                        result.data.chiriiRealizat = plainToClass<DuChiriiModel, object>(DuChiriiModel, result.data.chiriiRealizat || []);
                        result.data.estimatCASS = plainToClass(DuAnswers2024EstimatCASSModel, result.data.estimatCASS || { bifaCASS: false });
                        result.formData = plainToClass(ClientFormDataModel, result.formData);
                        result.formDataParameters = plainToClass(DuPfaParametersModel, result.formData.parameters);
                    } catch {
                        result = null;
                    }
                    this.spinner.hide();
                    return result;
                })
            );
    }

    public async updateClientFormDuPfaKeez2024Data(clientEid: string, doSubmit: boolean, data: DuPfa2024DataModel): Promise<boolean> {
		let mutation = new UpdateClientFormDuPfaKeez2024DataGQL(this.restService);

        let mutationData: Accounts_DuAnswers2024RepresentationInput = {
            persoana: {},
            activitatiIndependenteRealizat: [],
            chiriiRealizat: [],
            estimatCASS: {}
        };

        mutationData.persoana.adresa = data.persoana.adresa;
        mutationData.persoana.areHandicap = data.persoana.areHandicap;
        mutationData.persoana.cnp = data.persoana.cnp;
        mutationData.persoana.email = data.persoana.email;
        mutationData.persoana.iban = data.persoana.iban;
        mutationData.persoana.nerezident = data.persoana.nerezident;
        mutationData.persoana.nume = data.persoana.nume;
        mutationData.persoana.prenume = data.persoana.prenume;
        mutationData.persoana.telefon = data.persoana.telefon;

        mutationData.anRealizat = data.anRealizat;
        mutationData.rectificativaRealizat = data.rectificativaRealizat;
        mutationData.dreptAutorRealizat = data.dreptAutorRealizat;
        mutationData.agriRealizat = data.agriRealizat;
        mutationData.dividendeRealizat = data.dividendeRealizat;
        mutationData.alteSurseRealizat = data.alteSurseRealizat;
        mutationData.venituriStrainatate = data.venituriStrainatate;
        mutationData.cimNedeterminat = data.cimNedeterminat;
        mutationData.comments = data.comments;

        mutationData.estimatCASS.bifaCASS = data.estimatCASS.bifaCASS;
        mutationData.estimatCASS.optiuneCASS = data.estimatCASS.optiuneCASS;

        data.activitatiIndependenteRealizat.forEach((activitate) => {
            mutationData.activitatiIndependenteRealizat.push({
				cheltuieliDeductibile: activitate.cheltuieliDeductibile,
                codCaen: activitate.codCaen,
                denCaen: activitate.denCaen,
                dataDocument: activitate.dataDocument,
                dataInceperiiActivitatii: activitate.dataInceperiiActivitatii,
                dataIncheieriiActivitatii: activitate.dataIncheieriiActivitatii,
                formaOrganizare: activitate.formaOrganizare,
                normaDeVenit: activitate.normaDeVenit,
                numarDocument: activitate.numarDocument,
                sediuSauIdentificareBun: activitate.sediuSauIdentificareBun,
                sistemImpozitare: activitate.sistemImpozitare,
                venitBrut: activitate.venitBrut
			});
        });

        data.chiriiRealizat.forEach((chirie) => {
            mutationData.chiriiRealizat.push({
                adresa: chirie.adresa,
                dataDocument: chirie.dataDocument,
                dataInceperiiActivitatii: chirie.dataInceperiiActivitatii,
                dataIncetariiActivitatii: chirie.dataIncetariiActivitatii,
                numarDocument: chirie.numarDocument,
                sistemImpozitare: chirie.sistemImpozitare,
                venitBrut: chirie.venitBrut,
                cheltuieliDeductibile: chirie.cheltuieliDeductibile
            })
        });

        let result = await mutation.mutate({
			clientEid: clientEid,
            doSubmit: doSubmit,
            data: mutationData
		}, { error: true } as KMutationOptions).toPromise();

        if (result) {
			return true;
		}
		
		return false;
    }

    // Du PFA 2025
    public getDuPfaKeez2025Data(clientEid: string): Observable<DuPfa2025Model> {
        this.spinner.show();
        return this.restService
            .query({
                query: GetClientFormDuPfaKeez2025DataDocument,
                variables: {
                    clientEid: clientEid,
                },
                fetchPolicy: 'network-only'
            }, { spinner: true })
            .pipe(
                map((response: ApolloQueryResult<GetClientFormDuPfaKeez2025DataQuery>) => {
                    let result: DuPfa2025Model = null;
                    try {
                        result = plainToClass(DuPfa2025Model, response.data.accounts.getClientFormDuPfaKeez2025DataUsingGET);
                        result.data.persoana = plainToClass(DuPerson2025Model, result.data.persoana);
                        result.data = plainToClass(DuPfa2025DataModel, result.data);
                        result.data.activitatiIndependenteRealizat = plainToClass<DuActivitatiIndependente2025Model, object>(DuActivitatiIndependente2025Model, result.data.activitatiIndependenteRealizat || []);
                        result.data.chiriiRealizat = plainToClass<DuChiriiModel, object>(DuChiriiModel, result.data.chiriiRealizat || []);
                        result.data.coasigurati = plainToClass<DuCoasigurat2025Model, object>(DuCoasigurat2025Model, result.data.coasigurati || []);
                        result.data.obligRealizat = plainToClass(DuObligRealizat2025Model, result.data.obligRealizat);
                        result.formData = plainToClass(ClientFormDataModel, result.formData);
                        result.formDataParameters = plainToClass(DuPfaParametersModel, result.formData.parameters);
                    } catch {
                        result = null;
                    }
                    this.spinner.hide();
                    return result;
                })
            );
    }

    public async updateClientFormDuPfaKeez2025Data(clientEid: string, doSubmit: boolean, data: DuPfa2025DataModel): Promise<boolean> {
        let mutation = new UpdateClientFormDuPfaKeez2025DataGQL(this.restService);

        let mutationData: Accounts_DuAnswers2025RepresentationInput = {
            persoana: {
                nume: data.persoana.nume,
                prenume: data.persoana.prenume,
                initiala: data.persoana.initiala,
                cnp: data.persoana.cnp,
                adresa: data.persoana.adresa,
                telefon: data.persoana.telefon,
                email: data.persoana.email,
                iban: data.persoana.iban,
                areHandicap: data.persoana.areHandicap,
                nerezident: data.persoana.nerezident,
                statut: data.persoana.statut
            },
            anRealizat: data.anRealizat,
            lunaR: data.lunaR,
            rectificativaRealizat: data.rectificativaRealizat,
            totalPlataA: data.totalPlataA,
            bifa18: data.bifa18,
            bifa111: data.bifa111,
            bifa112: data.bifa112,
            bifa14: data.bifa14,
            bifa131: data.bifa131,
            bifa132: data.bifa132,
            dreptAutorRealizat: data.dreptAutorRealizat,
            agriRealizat: data.agriRealizat,
            titluriValoareRealizat: data.titluriValoareRealizat,
            dividendeRealizat: data.dividendeRealizat,
            alteSurseRealizat: data.alteSurseRealizat,
            optionalSanatateRealizat: data.optionalSanatateRealizat,
            optionalPensieRealizat: data.optionalPensieRealizat,
            venituriStrainatate: data.venituriStrainatate,
            cimNedeterminat: data.cimNedeterminat,
            impozitONG: data.impozitONG,
            ongCIF: data.ongCIF,
            ongIBAN: data.ongIBAN,
            ongName: data.ongName,
            comments: data.comments,
            obligRealizat: data.obligRealizat ? {
                cassTotalVenAi: data.obligRealizat.cassTotalVenAi,
                bazaCassDatoratAi: data.obligRealizat.bazaCassDatoratAi,
                cassAnualaAi: data.obligRealizat.cassAnualaAi,
                cassDatoratArt180Ai: data.obligRealizat.cassDatoratArt180Ai,
                cassDatoratAi: data.obligRealizat.cassDatoratAi,
                cassRetinutPlatitorAlin6Ai: data.obligRealizat.cassRetinutPlatitorAlin6Ai,
                cassDifPlusAi: data.obligRealizat.cassDifPlusAi,
                bifaCassDatoratAi: data.obligRealizat.bifaCassDatoratAi,
                oblimpozRealTotal: data.obligRealizat.oblimpozRealTotal,
                oblimpozRealAnticipat: data.obligRealizat.oblimpozRealAnticipat,
                oblimpozRealDifRestituit: data.obligRealizat.oblimpozRealDifRestituit,
                oblcasRealDifPlus: data.obligRealizat.oblcasRealDifPlus,
                oblcassRealDifPlusAi: data.obligRealizat.oblcassRealDifPlusAi,
                impozitVenitMinus: data.obligRealizat.impozitVenitMinus,
                casPlus: data.obligRealizat.casPlus,
                cassPlus: data.obligRealizat.cassPlus,
                difDePlata: data.obligRealizat.difDePlata
            } : {},
            coasigurati: (data.coasigurati || []).map(c => ({
                tipCoasigurat: c.tipCoasigurat,
                bazaCassCoasigurat: c.bazaCassCoasigurat,
                cassDatoratCoasigurat: c.cassDatoratCoasigurat
            })),
            chiriiRealizat: (data.chiriiRealizat || []).map(c => ({
                adresa: c.adresa,
                dataDocument: c.dataDocument,
                dataInceperiiActivitatii: c.dataInceperiiActivitatii,
                dataIncetariiActivitatii: c.dataIncetariiActivitatii,
                numarDocument: c.numarDocument,
                sistemImpozitare: c.sistemImpozitare,
                venitBrut: c.venitBrut,
                cheltuieliDeductibile: c.cheltuieliDeductibile
            })),
            activitatiIndependenteRealizat: (data.activitatiIndependenteRealizat || []).map(a => ({
                cheltuieliDeductibile: a.cheltuieliDeductibile,
                codCaen: a.codCaen,
                denCaen: a.denCaen,
                dataDocument: a.dataDocument,
                dataInceperiiActivitatii: a.dataInceperiiActivitatii,
                dataIncheieriiActivitatii: a.dataIncheieriiActivitatii,
                formaOrganizare: a.formaOrganizare,
                normaDeVenit: a.normaDeVenit,
                numarDocument: a.numarDocument?.toString(),
                sediuSauIdentificareBun: a.sediuSauIdentificareBun,
                sistemImpozitare: a.sistemImpozitare,
                venitBrut: a.venitBrut,
                categVenit: a.categVenit,
                denVenit: a.denVenit,
                detVenNet: a.detVenNet,
                pierderePrecedenta: a.pierderePrecedenta,
                pierdereCompensata: a.pierdereCompensata,
                venitRecalculat: a.venitRecalculat,
                impozitRetinut: a.impozitRetinut
            }))
        };

        let result = await mutation.mutate({
            clientEid: clientEid,
            doSubmit: doSubmit,
            data: mutationData
        }, { error: true } as KMutationOptions).toPromise();

        if (result) {
            return true;
        }

        return false;
    }

    public async generateDuPfaKeez(clientEid: string, instance: string) {
        return this.configService
            .getAccountsUrl()
            .pipe(
                switchMap((url) => {
                    url = url + `/clients/${clientEid}/forms/du-pfa-keez/${instance}/pdf`
                    return this.downloadService.download(url);
                }),
				take(1)
            )
			.subscribe();
	}

    public async generateDuPfaXml(clientEid: string, instance: string) {
        return this.configService
            .getAccountsUrl()
            .pipe(
                switchMap((url) => {
                    url = url + `/clients/${clientEid}/forms/du-pfa-keez/${instance}/xml`
                    return this.downloadService.download(url);
                }),
				take(1)
            )
			.subscribe();
	}

    public getClientFormsList(onlyDuPfa: boolean = false): Observable<ClientFormListModel> {
        let filter: string;
        if (onlyDuPfa) {
            filter = 'form.externalId[like]:du-pfa-keez'
        }
        
        return this.authService.getSelectedCompanyId()
			.pipe(
				switchMap((companyId) => {
					if (companyId) {
                        return this.restService
                            .query({
                                query: GetClientFormsDataDocument,
                                variables: {
                                    clientEid: companyId,
                                    filter: filter
                                },
                                fetchPolicy: 'network-only'
                            }, { spinner: true })
                            .pipe(
                                map((response: ApolloQueryResult<GetClientFormsDataQuery>) => {
                                    let result: ClientFormListModel = null;
                                    try {
                                        result = plainToClass(ClientFormListModel, response.data.accounts.getClientFormsDataUsingGET);
                                        result.data = plainToClass<ClientFormModel, object>(ClientFormModel, result.data || []);
                                    } catch {
                                        result = null;
                                    }
                                    return result;
                                })
                            );
                    }
                    return of(null);
				})
			);
    }

    public getDuPfaFormsList(): Observable<ClientFormListModel> {
        return this.getClientFormsList(true);
    }

    public formStatusDisplay(status: string): string {
		switch (status) {
			case FormStatus.Created:
				return 'Creat';
			case FormStatus.Draft:
				return 'În curs';
			case FormStatus.Submitted:
				return 'Trimis';
            case FormStatus.Deleted:
                return 'Șters';
            default:
                return '';
        }
	}

    public getFormRoutesAndComponents(formCode: string = null): Observable<FormComponentModel[]> {
        let query = new GetFormComponentsGQL(this.restService);
		return query.fetch(
			{
				formCode: formCode,
			}
		).pipe(
            map((queryData) => {
                if (queryData != null) {
                    let result = plainToClass<FormComponentModel, object>(FormComponentModel, queryData.data.accounts.getFormComponentsUsingGET || []);
                    return result;
                }
                return null;
            })
        );
	}
}
